import { Link } from "@tanstack/react-router";
import { Phone, Mail, Facebook, MapPin } from "lucide-react";
import { BUSINESS, services, serviceLink, cityLink } from "@/lib/site";
import { Logo } from "./Logo";
import { WaveDivider } from "./WaveDivider";

export function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="surface-deep grain relative mt-auto text-deep-foreground">
      <WaveDivider variant="toBlush" className="absolute -top-[60px] left-0 md:-top-[90px]" />
      <div className="relative z-[1] mx-auto max-w-6xl px-4 py-16 md:px-6 md:py-20">
        {/* faint script watermark */}
        <span
          className="pointer-events-none absolute right-4 top-8 select-none font-script text-[18vw] leading-none text-white/5 md:text-[10rem]"
          aria-hidden
        >
          Pretty in Pink
        </span>

        <div className="relative grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-4">
            {/* LOGO SPOT: real round badge logo image */}
            <Logo tone="light" size="lg" />
            <p className="font-script text-2xl text-pink-soft">{BUSINESS.tagline}</p>
            <p className="max-w-xs text-sm text-white/70">
              {BUSINESS.secondary} {BUSINESS.offers} in {BUSINESS.city}, {BUSINESS.state} and
              surrounding areas.
            </p>
            <a
              href={BUSINESS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform hover:scale-105"
              aria-label="Visit Pretty in Pink Cleaning Service on Facebook"
            >
              <Facebook className="h-5 w-5" />
            </a>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white">Quick Links</h3>
            <ul className="mt-4 space-y-2 text-sm">
              <FooterLink to="/" label="Home" />
              <FooterLink to="/services" label="Services" />
              <FooterLink to="/service-areas" label="Service Areas" />
              <FooterLink to="/gallery" label="Gallery" />
              <FooterLink to="/about" label="About" />
              <FooterLink to="/faq" label="FAQ" />
              <FooterLink to="/contact" label="Contact" />
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white">Services</h3>
            <ul className="mt-4 space-y-2 text-sm">
              {services.map((s) => (
                <FooterLink key={s.slug} {...serviceLink(s.slug)} label={s.name} />
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white">Service Areas</h3>
            <ul className="mt-4 space-y-2 text-sm">
              {BUSINESS.areaServed.map((c) => {
                const slug = c
                  .toLowerCase()
                  .replace(/, al$/, "-al")
                  .replace(/[^a-z-]/g, "") as
                  | "wetumpka-al"
                  | "millbrook-al"
                  | "prattville-al"
                  | "montgomery-al"
                  | "tallassee-al"
                  | "eclectic-al";
                return <FooterLink key={c} {...cityLink(slug)} label={c} />;
              })}
            </ul>
            <div className="mt-5 space-y-2 text-sm">
              <a
                href={BUSINESS.phoneHref}
                className="flex items-center gap-2 text-white/80 hover:text-pink-soft"
              >
                <Phone className="h-4 w-4 text-pink-soft" />
                {BUSINESS.phoneDisplay}
              </a>
              <a
                href={BUSINESS.emailHref}
                className="flex items-center gap-2 break-all text-white/80 hover:text-pink-soft"
              >
                <Mail className="h-4 w-4 text-pink-soft" />
                {BUSINESS.email}
              </a>
              <p className="flex items-center gap-2 text-white/60">
                <MapPin className="h-4 w-4 text-pink-soft" />
                Serving Wetumpka &amp; Surrounding Areas
              </p>
            </div>
          </div>
        </div>

        <div className="relative mt-12 border-t border-white/10 pt-6 text-center text-xs text-white/50">
          © {year} {BUSINESS.name}. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, label }: { to: string; label: string }) {
  return (
    <li>
      <Link to={to as "/"} className="text-white/70 transition-colors hover:text-pink-soft">
        {label}
      </Link>
    </li>
  );
}
